import React from 'react';
import './KcStatusPills.css';

function pillClassName(variant, className) {
  return ['kc-pill', `kc-pill--${variant}`, className].filter(Boolean).join(' ');
}

export function HeartbeatGlyph() {
  return (
    <svg
      className="kc-pill__icon"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M2 12h4l2.5-6 4 12 2.5-6h7" />
    </svg>
  );
}

export function DropletGlyph() {
  return (
    <svg
      className="kc-pill__icon"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M12 3s6 6.6 6 11a6 6 0 0 1-12 0c0-4.4 6-11 6-11z" />
    </svg>
  );
}

export function PotassiumPill({ readout, className = '', ...props }) {
  return (
    <div className={pillClassName('potassium', className)} {...props}>
      <span aria-hidden="true" className="kc-kglyph">K<sup>+</sup></span>
      <span className="kc-readout">{readout}</span>
    </div>
  );
}

// The K+ pill in its `.kc-tag` chassis (relative-positioned, cursor:help).
//
// The bespoke `.kc-panel` sub-tooltip that used to live here was REMOVED 2026-07-25
// (Edward: the toast's embedded tooltips "look like reconstructions rather than
// references to the authoritative truth"). It was a hand-written two-line panel
// standing in for the real combat-rail potassium tooltip. Callers now wrap this pill
// in the rail's own panel via `htnPotassiumTooltipProps` (PlayerPotassiumTag), so
// there is ONE potassium tooltip definition. Do not re-add a local panel here: that
// is precisely how the surfaces drifted apart the first time.
//
// `interactive` is retained as an accepted no-op prop so the rail's existing
// interactive={false} call sites keep working; it only ever controlled the removed
// panel's focusability.
export function PotassiumTag({ readout, interactive = false }) {
  return (
    <div
      className="kc-tag"
      role="img"
      aria-label="Potassium continuum"
      style={{ '--effect-color': 'rgb(248,113,113)' }}
    >
      <PotassiumPill readout={readout} />
    </div>
  );
}

// `beat` (Edward 2026-07-25): opt-in. When set to a nonzero HR net, the heartbeat
// glyph THROBS at a rate mapped to that net (tachy = faster, brady = slower) and the
// numeric "+N" readout is dropped on the combat rail — the felt rate replaces the
// number ("instead of the +1"). Consumers that omit `beat` render the static glyph
// unchanged (DrugInfluenceRows, the hover tooltip's own detail pill).
export function hrBeatDurationMs(net) {
  const n = Number.isFinite(Number(net)) ? Math.trunc(Number(net)) : 0;
  // Shay's rate spec (2026-07-25): the beat rate is `beats / 2` per second, where
  // `beats = max(2, 3 + net)` mirrors the engine's compute_batch_size. So the period
  // is 2000 / beats ms — brady 2b=1000ms, steady 3b=667ms, tachy+1 4b=500ms,
  // tachy+2 5b=400ms. A faster heart dispenses more beats AND throbs proportionally
  // faster; the felt cadence tracks the batch it fires.
  const beats = Math.max(2, 3 + n);
  return Math.round(2000 / beats);
}

// `suffix` (2026-07-28): opt-in trailing node, rendered after the label inside the
// same pill. It exists for the delayed-activation primitive — a mechanic that has
// been drafted but has not started yet rides a clock + countdown on its own status
// pill, so the rail can say "this is coming" instead of staying silent until the
// round it fires. Omitted everywhere else, so every existing pill is unchanged.
export function HeartRatePill({ label = 'HR', className = '', beat = null, suffix = null, style, ...props }) {
  const beatNet = Number.isFinite(Number(beat)) ? Math.trunc(Number(beat)) : 0;
  const beating = beat !== null && beat !== undefined && beatNet !== 0;
  const mergedStyle = beating
    ? { ...(style || {}), '--hr-beat-duration': `${hrBeatDurationMs(beatNet)}ms` }
    : style;
  return (
    <div
      className={pillClassName('hr', [beating ? 'kc-pill--hr-beating' : '', className].filter(Boolean).join(' '))}
      data-beat={beating ? String(beatNet) : undefined}
      style={mergedStyle}
      {...props}
    >
      <HeartbeatGlyph />
      <span className="kc-hrglyph">{label}</span>
      {suffix ? <span className="kc-pill__suffix">{suffix}</span> : null}
    </div>
  );
}

export function DiuresisPill({ label = 'Diuresis', className = '', ...props }) {
  return (
    <div className={pillClassName('diuresis', className)} {...props}>
      <DropletGlyph />
      <span className="kc-hrglyph">{label}</span>
    </div>
  );
}

// Vasodilation — a vessel bowing OPEN. Two walls that bulge apart across the middle,
// which is the whole mechanic in one mark: the lumen widens, so the pressure the
// enemy tries to apply has somewhere to go.
export function VasodilationGlyph() {
  return (
    <svg
      className="kc-pill__icon"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M3 7c4 0 4.5-2.5 9-2.5S17 7 21 7" />
      <path d="M3 17c4 0 4.5 2.5 9 2.5s5-2.5 9-2.5" />
    </svg>
  );
}

// `charges` is opt-in. The drug toast states the card's AUTHORED charge count; the
// combat rail states what is LEFT. Same pill either way, so the two never drift into
// looking like different mechanics.
export function VasodilationPill({ label = 'Vasodilation', charges = null, className = '', ...props }) {
  const count = Number.isFinite(Number(charges)) ? Math.max(0, Math.trunc(Number(charges))) : null;
  return (
    <div className={pillClassName('vasodilation', className)} {...props}>
      <VasodilationGlyph />
      <span className="kc-hrglyph">{label}</span>
      {count !== null ? <span className="kc-pill__count">{count}</span> : null}
    </div>
  );
}
